import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import pt from "./locales/pt.json";
import sw from "./locales/sw.json";
import hi from "./locales/hi.json";
import zh from "./locales/zh.json";
import ru from "./locales/ru.json";
import ig from "./locales/ig.json";

export const LANGUAGES = [
  { code: "en", name: "English", flag: "GB", nativeName: "English" },
  { code: "es", name: "Español", flag: "ES", nativeName: "Español" },
  { code: "fr", name: "Français", flag: "FR", nativeName: "Français" },
  { code: "pt", name: "Português", flag: "PT", nativeName: "Português" },
  { code: "sw", name: "Kiswahili", flag: "KE", nativeName: "Kiswahili" },
  { code: "hi", name: "हिन्दी", flag: "IN", nativeName: "हिन्दी" },
  { code: "zh", name: "中文", flag: "CN", nativeName: "中文" },
  { code: "ru", name: "Русский", flag: "RU", nativeName: "Русский" },
  { code: "ig", name: "Igbo", flag: "NG", nativeName: "Asụsụ Igbo" },
];

// Supported language codes for detection
const SUPPORTED_CODES = LANGUAGES.map(l => l.code);

// Map browser language variants to our supported languages
// e.g., zh-CN, zh-TW, zh-HK -> zh | pt-BR, pt-PT -> pt | es-MX, es-AR -> es
const LANGUAGE_MAPPINGS = {
  "zh-CN": "zh", "zh-TW": "zh", "zh-HK": "zh", "zh-Hans": "zh", "zh-Hant": "zh",
  "pt-BR": "pt", "pt-PT": "pt",
  "es-MX": "es", "es-AR": "es", "es-CO": "es", "es-419": "es",
  "en-US": "en", "en-GB": "en", "en-AU": "en", "en-CA": "en", "en-IN": "en",
  "fr-CA": "fr", "fr-BE": "fr", "fr-CH": "fr",
  "ru-RU": "ru", "ru-UA": "ru",
  "hi-IN": "hi",
};

// Custom language detector that handles regional variants
const customLanguageDetector = {
  name: "customNavigator",
  lookup() {
    // Check if user already has a saved preference
    const saved = localStorage.getItem("cognifyai_lang");
    if (saved && SUPPORTED_CODES.includes(saved)) {
      return saved;
    }

    // Get browser languages (ordered by preference)
    const browserLangs = navigator.languages || [navigator.language];
    
    for (const lang of browserLangs) {
      // Check exact match first
      if (SUPPORTED_CODES.includes(lang)) {
        return lang;
      }
      
      // Check mapped variants (e.g., zh-CN -> zh)
      if (LANGUAGE_MAPPINGS[lang]) {
        return LANGUAGE_MAPPINGS[lang];
      }
      
      // Check base language code (e.g., "en-US" -> "en")
      const baseLang = lang.split("-")[0];
      if (SUPPORTED_CODES.includes(baseLang)) {
        return baseLang;
      }
    }
    
    return "en"; // Default fallback
  },
  cacheUserLanguage(lng) {
    localStorage.setItem("cognifyai_lang", lng);
  }
};

// Create language detector instance with custom detector
const languageDetector = new LanguageDetector();
languageDetector.addDetector(customLanguageDetector);

// Track if this is first visit (for welcome message)
const isFirstVisit = !localStorage.getItem("cognifyai_lang");
const detectedLanguage = customLanguageDetector.lookup();

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      pt: { translation: pt },
      sw: { translation: sw },
      hi: { translation: hi },
      zh: { translation: zh },
      ru: { translation: ru },
      ig: { translation: ig },
    },
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    detection: {
      order: ["customNavigator", "localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "cognifyai_lang",
    },
  });

// Export helper to check if language was auto-detected on first visit
export const getAutoDetectionInfo = () => {
  const detected = LANGUAGES.find(l => l.code === detectedLanguage);
  return {
    isFirstVisit,
    detectedLanguage,
    languageName: detected?.nativeName || detected?.name || "English",
    wasAutoDetected: isFirstVisit && detectedLanguage !== "en"
  };
};

// Helper to get language by code
export const getLanguageByCode = (code) => LANGUAGES.find(l => l.code === code);

export default i18n;
